import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import Footer from "@/pages/home/components/Footer";
import JsonLd from "@/components/base/JsonLd";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://cssbuyspreadsheet.my";

const categories = [
  { name: "Calçados", icon: "ri-footprint-line" },
  { name: "Moletons", icon: "ri-t-shirt-line" },
  { name: "Camisetas", icon: "ri-t-shirt-line" },
  { name: "Jaquetas", icon: "ri-t-shirt-air-line" },
  { name: "Calças/Shorts", icon: "ri-align-bottom" },
  { name: "Bolsas", icon: "ri-handbag-line" },
  { name: "Acessórios", icon: "ri-glasses-line" },
  { name: "Perfumes", icon: "ri-contrast-drop-line" },
];

interface ArtigoPlaceholderProps {
  title: string;
  desc: string;
  icon: string;
  slug: string;
}

function buildArticleSchema(title: string, desc: string, slug: string) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": desc,
    "author": { "@type": "Organization", "name": "CSSBuy Spreadsheet" },
    "publisher": { "@type": "Organization", "name": "CSSBuy Spreadsheet", "url": siteUrl },
    "datePublished": "2026-06-17",
    "dateModified": "2026-06-17",
    "inLanguage": "pt-PT",
    "mainEntityOfPage": { "@type": "WebPage", "@id": `${siteUrl}/${slug}` },
  };
}

export default function ArtigoPlaceholder({ title, desc, icon, slug }: ArtigoPlaceholderProps) {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = `${title} | CSSBuy Spreadsheet`;
  }, [title]);

  const articleData = buildArticleSchema(title, desc, slug);

  return (
    <>
      <JsonLd data={articleData as unknown as Record<string, unknown>} />
      <nav className="fixed top-0 left-0 w-full z-50 bg-background-50 border-b border-background-200">
        <div className="max-w-4xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm text-foreground-600 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i> Voltar ao Início
          </button>
          <span className="text-sm font-heading font-semibold text-foreground-900">CSSBuy Spreadsheet</span>
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="px-4 py-1.5 rounded-full bg-primary-500 text-background-50 text-xs font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Comprar Agora
          </a>
        </div>
      </nav>

      <main className="pt-20 pb-12">
        <article className="max-w-3xl mx-auto px-4 md:px-6">
          <header className="mb-8">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium uppercase tracking-wider mb-4">
              <i className={icon}></i> Guia CSSBuy
            </span>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mb-3 leading-tight">
              {title}
            </h1>
            <p className="text-sm text-foreground-600 max-w-2xl">
              {desc}
            </p>
          </header>

          <div className="bg-background-100 rounded-2xl p-8 md:p-10 text-center mb-10">
            <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-5">
              <i className={`${icon} text-2xl text-primary-600`}></i>
            </div>
            <h2 className="text-lg font-heading font-semibold text-foreground-950 mb-3">
              Conteúdo em Breve
            </h2>
            <p className="text-sm text-foreground-600 max-w-md mx-auto mb-6 leading-relaxed">
              Estamos preparando um guia completo e detalhado sobre {title.toLowerCase()}. Enquanto isso, explore nossas outras categorias e comece a economizar nas suas compras da China.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6">
              {categories.map((cat, idx) => (
                <a
                  key={idx}
                  href="/#categories"
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-background-50 hover:bg-background-200/70 transition-colors text-xs text-foreground-600 hover:text-foreground-900 cursor-pointer whitespace-nowrap"
                >
                  <i className={`${cat.icon} text-xs`}></i>
                  {cat.name}
                </a>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Acessar Guia Completo <i className="ri-arrow-right-up-line"></i>
              </a>
              <button
                onClick={() => navigate("/")}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-background-50 text-foreground-700 text-sm font-medium hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer border border-background-200"
              >
                <i className="ri-home-line"></i> Voltar ao Início
              </button>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}